import React, { useState } from "react"
import Navbar from "../../components/Navbar/Navbar"
import {
  Table,
  Select,
  Button,
  Modal,
  Row,
  Col,
  Form,
  DatePicker,
  TimePicker,
  Input,
  Checkbox,
  Tabs,
} from "antd"
import {
  EditOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons"
import dayjs from "dayjs"
import moment from "moment"
import { UseGetRequest } from "../../utils/api/ApiRequests"
import { GetAllEmployees, GetAllProjects } from "../../utils/api/DropdownFiltersApi"
import { usePostRequest } from "../../hooks/usePostRequest"
import { getIsAdminLogin, getIsEmployeeLogin, getEmployeeId, capitalizeEachWord } from "../../utils/Common"
import toast from "react-hot-toast"
import * as XLSX from "xlsx"

const AddSubmitButton = ({ form, isLoading }) => {
  const [submittable, setSubmittable] = useState(false)
  const values = Form.useWatch([], form)
  React.useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(
        () => {
          setSubmittable(true)
        },
        () => {
          setSubmittable(false)
        }
      )
  }, [values, form])

  return (
    <Button
      type="primary"
      htmlType="submit"
      disabled={!submittable}
      loading={isLoading}
    >
      Submit
    </Button>
  )
}

export default function Overtime() {
  const [form] = Form.useForm()
  const isAdminLogin = getIsAdminLogin()
  const [tableData, setTableData] = useState([])
  const [isFetching, setIsFetching] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [clickedRowData, setClickedRowData] = useState({})
  const [projectOptions, setProjectOptions] = useState([])
  const [projectDetails, setProjectDetails] = useState({})
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [modal, contextHolder] = Modal.useModal()
  const employeeOptions = GetAllEmployees()
  const allProjects = GetAllProjects()
  const dateFormat = "YYYY-MM-DD"
  const [searchTerm, setSearchTerm] = useState("")
  const [reason, setReason] = useState("")
  const [totalHours, setTotalHours] = useState("")
  const isEmployeeLogin = getIsEmployeeLogin()
  const employeeId = getEmployeeId()

  const { callPost, isLoading } = usePostRequest(() => {
    setShowAddModal(false)
    setShowEditModal(false)
    setShowViewModal(false)
    setShowConfirmDelete(false)
    resetValues()
    setFilterConfig((prev) => ({ ...prev, is_updated: !prev.is_updated }))
  })

  const [filterConfig, setFilterConfig] = useState({
    is_updated: false,
    status: "pending",
    employee_id: isAdminLogin ? null : employeeId,
  })

  const computeTotalHours = () => {
    const dateFrom = form.getFieldValue("date_from") || form.getFieldValue("date")
    const dateTo = form.getFieldValue("date_to") || dateFrom
    const timeFrom = form.getFieldValue("overtime")
    const timeTo = form.getFieldValue("overtime_to")

    if (dateFrom && dateTo && timeFrom && timeTo) {
      const startStr = `${dateFrom.format("YYYY-MM-DD")} ${timeFrom.format("HH:mm:ss")}`
      const endStr = `${dateTo.format("YYYY-MM-DD")} ${timeTo.format("HH:mm:ss")}`
      const start = dayjs(startStr)
      const end = dayjs(endStr)

      let diffMins = end.diff(start, "minute")
      if (diffMins > 0) {
        const hrs = Math.floor(diffMins / 60)
        const mins = diffMins % 60
        setTotalHours(`${hrs}h ${mins}m`)
      } else {
        setTotalHours("0h 0m")
      }
    } else {
      setTotalHours("")
    }
  }

  const tableColumns = [
    {
      title: "Employee",
      dataIndex: "full_name",
      key: "full_name",
    },
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
    },
    {
      title: "Date From",
      dataIndex: "date",
      key: "date",
      render: (date) => <div>{date ? moment(date).format("MMM DD, YYYY") : "-"}</div>,
    },
    {
      title: "Date To",
      dataIndex: "date_to",
      key: "date_to",
      render: (dateTo, record) => (
        <div>{moment(dateTo || record.date).format("MMM DD, YYYY")}</div>
      ),
    },
    {
      title: "Overtime From",
      dataIndex: "overtime",
      key: "overtime_from",
      render: (overtime) => (
        <div>{overtime ? moment(overtime, "HH:mm:ss").format("hh:mm A") : "-"}</div>
      ),
    },
    {
      title: "Overtime To",
      dataIndex: "overtime_to",
      key: "overtime_to",
      render: (overtimeTo) => (
        <div>{overtimeTo ? moment(overtimeTo, "HH:mm:ss").format("hh:mm A") : "-"}</div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <div>{capitalizeEachWord(status || "")}</div>,
    },
  ]

  const onFinish = async (formInputs) => {
    const dateFromStr = formInputs.date_from ? formInputs.date_from.format(dateFormat) : (formInputs.date ? formInputs.date.format(dateFormat) : "")
    const dateToStr = formInputs.date_to ? formInputs.date_to.format(dateFormat) : dateFromStr

    formInputs.date = dateFromStr
    formInputs.date_from = dateFromStr
    formInputs.date_to = dateToStr
    formInputs.overtime = formInputs.overtime.format("HH:mm:ss")
    formInputs.overtime_to = formInputs.overtime_to.format("HH:mm:ss")
    formInputs.status = "pending"
    formInputs.is_timein = formInputs.is_timein ? "1" : "0"

    callPost("overtimes/add", formInputs, false)
  }

  const onEdit = async (formInputs) => {
    const dateFromStr = formInputs.date_from ? formInputs.date_from.format(dateFormat) : (formInputs.date ? formInputs.date.format(dateFormat) : "")
    const dateToStr = formInputs.date_to ? formInputs.date_to.format(dateFormat) : dateFromStr

    formInputs.date = dateFromStr
    formInputs.date_from = dateFromStr
    formInputs.date_to = dateToStr
    formInputs.overtime = formInputs.overtime.format("HH:mm:ss")
    formInputs.overtime_to = formInputs.overtime_to.format("HH:mm:ss")
    formInputs.is_timein = formInputs.is_timein ? "1" : "0"

    callPost(`overtimes/update/` + clickedRowData.id, formInputs, false)
  }

  function exportToExcel() {
    const excelData = [tableColumns.map((col) => col.title)]
    tableData.forEach((data) => {
      let tempArray = tableColumns.map((col) => data[col.key])
      excelData.push(tempArray)
    })
    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.aoa_to_sheet(excelData)
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1")
    const excelBlob = new Blob(
      [XLSX.write(workbook, { bookType: "xlsx", type: "array" })],
      { type: "application/octet-stream" }
    )
    const excelUrl = URL.createObjectURL(excelBlob)
    const downloadAnchor = document.createElement("a")
    downloadAnchor.href = excelUrl
    downloadAnchor.download = "Employee Overtime.xlsx"
    downloadAnchor.click()
    setTimeout(() => {
      URL.revokeObjectURL(excelUrl)
    }, 100)
  }

  const confirmApprove = (overtime, status) => {
    modal.confirm({
      title: `Are you sure you want to ${status} ${overtime.full_name}'s overtime?`,
      icon: <CheckCircleOutlined />,
      okText: "Confirm",
      cancelText: "Cancel",
      onOk: () => handleApprove(overtime.id, status),
    })
  }

  function handleApprove(passedId, status) {
    const newStatus = `${status}d`
    callPost(
      "overtimes/approve",
      { overtime_id: passedId, status: newStatus },
      false
    )
    setShowViewModal(false)
    resetValues()
    setFilterConfig((prev) => ({ ...prev, is_updated: !prev.is_updated }))
  }

  function handleDelete(passedId) {
    callPost(
      "overtimes/delete",
      { overtime_id: passedId, remarks: reason },
      false
    )
    resetValues()
  }

  function resetValues() {
    form.resetFields()
    setReason("")
    setProjectOptions([])
    setProjectDetails({})
    setTotalHours("")
  }

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterConfig((prev) => ({ ...prev, term: searchTerm }))
    }
  }

  const handleRowClick = (rowData) => {
    setClickedRowData(rowData)
    fetchProjectOptions(rowData.employee_id)
    fetchProjectDetails(rowData.project_id)
    const formattedValues = { ...rowData }
    const dFrom = rowData.date_from || rowData.date
    const dTo = rowData.date_to || dFrom
    formattedValues.date_from = dFrom ? dayjs(dFrom, dateFormat) : ""
    formattedValues.date_to = dTo ? dayjs(dTo, dateFormat) : ""
    formattedValues.date = formattedValues.date_from
    formattedValues.overtime = rowData.overtime ? dayjs(rowData.overtime, "HH:mm:ss") : null
    formattedValues.overtime_to = rowData.overtime_to ? dayjs(rowData.overtime_to, "HH:mm:ss") : null
    formattedValues.is_timein = rowData.is_timein === "1" ? true : false
    form.setFieldsValue(formattedValues)
    setShowViewModal(true)
    setTimeout(() => computeTotalHours(), 200)
  }

  const rowProps = (record) => ({
    onClick: () => handleRowClick(record),
  })

  async function fetchProjectOptions(passedId) {
    if (!passedId) return
    setProjectOptions([])
    setProjectDetails({})

    const response = await UseGetRequest("employees/get_projects", {
      employee_id: passedId,
    })

    if (
      response.data &&
      response.data.response &&
      response.data.response.employee_projects &&
      response.data.response.employee_projects.length > 0
    ) {
      const res = response.data.response.employee_projects.map((row) => {
        return {
          value: row.id,
          label: row.name,
        }
      })
      setProjectOptions(res)
    } else {
      setProjectOptions([])
    }
  }

  async function fetchProjectDetails(id) {
    if (!id) return
    const response = await UseGetRequest("projects/get", { project_id: id })
    if (response.data && response.data.data && response.data.data.length > 0) {
      setProjectDetails(response.data.data[0])
    }
  }

  React.useEffect(() => {
    async function fetchEmployeeData() {
      if (isEmployeeLogin && employeeId) {
        fetchProjectOptions(employeeId)
        form.setFieldsValue({ employee_id: Number(employeeId) })
      }
    }
    fetchEmployeeData()
  }, [isEmployeeLogin, employeeId])

  React.useEffect(() => {
    async function fetchTableData() {
      setIsFetching(true)
      setTableData([])
      const response = await UseGetRequest("overtimes/get", filterConfig)
      if (response.data) {
        setTableData(response.data.overtimes || [])
      }
      setIsFetching(false)
    }

    fetchTableData()
  }, [filterConfig])

  return (
    <Navbar
      activeNav="overtime"
      pageTitle="Overtimes"
      isDownloadBtn
      pageButtonTitle2="Add"
      onClickButton={() => {
        setShowAddModal(true)
        setShowViewModal(false)
        resetValues()
        if (isEmployeeLogin && employeeId) {
          form.setFieldsValue({ employee_id: Number(employeeId) })
          fetchProjectOptions(employeeId)
        }
      }}
      pageButtonTitle="Download Excel"
      onClickDownloadButton={exportToExcel}
      searchPlaceholder="employee"
      setSearchQuery={(e) => setSearchTerm(e)}
      onSearch={handleSearch}
      isManager
      searchable={isAdminLogin}
    >
      {contextHolder}
      <Tabs
        defaultActiveKey="pending"
        className="mt-4"
        items={[
          { key: "pending", label: "Pending" },
          { key: "approved", label: "Approved" },
          { key: "disapproved", label: "Disapproved" },
          { key: "", label: "All" },
        ]}
        type="card"
        onChange={(e) => setFilterConfig((prev) => ({ ...prev, status: e }))}
      />
      <Table
        className=""
        rowKey="id"
        columns={tableColumns}
        dataSource={tableData}
        loading={isFetching}
        onRow={(record) => ({
          ...rowProps(record),
          style: { cursor: "pointer" },
        })}
      />
      <Modal
        maskClosable={false}
        title={`${showAddModal ? "Add" : "Edit"} Overtime`}
        centered
        open={showAddModal || showEditModal}
        footer={null}
        onCancel={() => {
          setShowAddModal(false)
          setShowEditModal(false)
          resetValues()
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="validateOnly"
          layout="vertical"
          onFinish={showAddModal ? onFinish : onEdit}
          initialValues={{ is_timein: false }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="employee_id"
                label="Employee"
                rules={[{ required: true, message: "Please select an employee!" }]}
              >
                {isEmployeeLogin ? (
                  <Select
                    options={employeeOptions.slice(1)}
                    disabled
                    className="secondary-bg"
                  />
                ) : (
                  <Select
                    options={employeeOptions.slice(1)}
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input?.toLowerCase())
                    }
                    onChange={(e) => fetchProjectOptions(e)}
                    showSearch
                    placeholder="Select an employee"
                  />
                )}
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="project_id"
                label="Project"
                rules={[{ required: true, message: "Please select a project!" }]}
              >
                <Select
                  options={projectOptions.length > 0 ? projectOptions : allProjects}
                  placeholder="Select a project"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input?.toLowerCase())
                  }
                  onChange={(e, obj) => {
                    setProjectDetails({ ...obj, id: e })
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="date_from"
                label="Date From"
                rules={[{ required: true, message: "Please select Date From!" }]}
              >
                <DatePicker
                  className="w-100"
                  placeholder="YYYY-MM-DD"
                  onChange={(val) => {
                    if (form.getFieldValue("same_date") && val) {
                      form.setFieldValue("date_to", val)
                    }
                    computeTotalHours()
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="overtime"
                label="Overtime From"
                rules={[{ required: true, message: "Please select Overtime From!" }]}
              >
                <TimePicker
                  className="w-100"
                  format={"hh:mm A"}
                  onChange={() => computeTotalHours()}
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="date_to"
                label="Date To"
                rules={[{ required: true, message: "Please select Date To!" }]}
                className="mb-1"
              >
                <DatePicker
                  className="w-100"
                  placeholder="YYYY-MM-DD"
                  onChange={() => computeTotalHours()}
                />
              </Form.Item>
              <Form.Item name="same_date" valuePropName="checked" className="mb-3">
                <Checkbox
                  onChange={(e) => {
                    if (e.target.checked) {
                      const dateFrom = form.getFieldValue("date_from")
                      if (dateFrom) {
                        form.setFieldValue("date_to", dateFrom)
                        computeTotalHours()
                      }
                    }
                  }}
                >
                  <small>Same date as Date From</small>
                </Checkbox>
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="overtime_to"
                label="Overtime To"
                rules={[{ required: true, message: "Please select Overtime To!" }]}
              >
                <TimePicker
                  className="w-100"
                  format={"hh:mm A"}
                  onChange={() => computeTotalHours()}
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item label="Total Hours">
                <Input
                  value={totalHours}
                  readOnly
                  className="secondary-bg"
                  placeholder="Auto-computed"
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={24}>
              <Form.Item
                name="reason"
                label="Reason"
                rules={[{ required: true, message: "Please enter reason for overtime!" }]}
              >
                <Input.TextArea rows={4} placeholder="Enter reason for overtime" />
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end" className="mt-3">
            <Button
              className="me-2"
              danger
              onClick={() => {
                setShowAddModal(false)
                setShowEditModal(false)
                resetValues()
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <AddSubmitButton form={form} isLoading={isLoading} />
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Review Overtime"
        centered
        open={showViewModal}
        footer={null}
        onCancel={() => {
          setShowViewModal(false)
          resetValues()
        }}
        width={800}
      >
        <Form className="mt-3 mx-3" layout="vertical">
          <Row gutter={[16, 0]}>
            <Col xs={24} lg={12}>
              <Form.Item label="Employee">
                <Input
                  value={clickedRowData.full_name}
                  disabled
                  className="secondary-bg"
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Project">
                <Input
                  value={clickedRowData.project}
                  disabled
                  className="secondary-bg"
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Date From">
                <Input
                  value={clickedRowData.date ? moment(clickedRowData.date).format("MMM DD, YYYY") : ""}
                  disabled
                  className="secondary-bg"
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Overtime From">
                <Input
                  value={clickedRowData.overtime ? moment(clickedRowData.overtime, "HH:mm:ss").format("hh:mm A") : ""}
                  disabled
                  className="secondary-bg"
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Date To">
                <Input
                  value={moment(clickedRowData.date_to || clickedRowData.date).format("MMM DD, YYYY")}
                  disabled
                  className="secondary-bg"
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Overtime To">
                <Input
                  value={clickedRowData.overtime_to ? moment(clickedRowData.overtime_to, "HH:mm:ss").format("hh:mm A") : ""}
                  disabled
                  className="secondary-bg"
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Total Hours">
                <Input
                  value={totalHours}
                  disabled
                  className="secondary-bg"
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item label="Reason">
                <Input.TextArea
                  value={clickedRowData.reason}
                  disabled
                  className="secondary-bg"
                  rows={4}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end" className="mt-3">
            <Button
              className="me-2"
              onClick={() => {
                setShowViewModal(false)
                resetValues()
              }}
            >
              Close
            </Button>
            {isAdminLogin && clickedRowData.status === "pending" && (
              <>
                <Button
                  className="me-2"
                  type="primary"
                  onClick={() => confirmApprove(clickedRowData, "approve")}
                >
                  Approve
                </Button>
                <Button
                  danger
                  onClick={() => confirmApprove(clickedRowData, "disapprove")}
                >
                  Disapprove
                </Button>
              </>
            )}
          </Row>
        </Form>
      </Modal>
    </Navbar>
  )
}